import { isNewOptionValid } from '~/domain/configurationRule';
import { IConfigurationOptionGroup } from '~/domain/IConfigurationOptionGroup';
import { IConfigurationRule } from '~/domain/IConfigurationRule';

const getDefaultConfiguration = (packSet: IConfigurationOptionGroup, index = 0): string => {
  if (packSet.packs.length > index) {
    return packSet.packs[index].code;
  }
  return '';
}

/**
 * Return suggested configuration if there is one that matches the specified packSet
 * @param packSet
 * @param suggestedFeatures
 */
const getSuggestedConfiguration = (packSet: IConfigurationOptionGroup, suggestedFeatures: string[]) => {
  const suggestedPack = packSet.packs.find(pack => suggestedFeatures.find(suggestedFeature => suggestedFeature === pack.code));
  return suggestedPack?.code;
}

/**
 * Get default configurations based on given pack sets, rules, existing features (and their corresponding pack sets)
 * Default configuration tries to go down the pack set options in the order of the option definition based on being able to satisfy all rules
 * @param packSets All known pack sets
 * @param rules All rules that configuration has to satisfy
 * @param existingFeatures List of features that have to exist in the returned configuration
 * @param existingFeaturePackSets List of pack sets corresponding to list of existing features
 * @param suggestedFeatures List of feature suggestions that should be tried first but can be changed
 */
export const getDefaultConfigurations = (
  packSets: IConfigurationOptionGroup[],
  rules: IConfigurationRule[],
  existingFeatures: string[] = [],
  existingFeaturePackSets: IConfigurationOptionGroup[] = [],
  suggestedFeatures: string[] = []
) : string[] => {
  const config = new Map<string, string>();

  existingFeatures.forEach((existingFeature) => {
    const existingPackSet = existingFeaturePackSets.find(
      packSet => packSet.packs.find(pack => pack.code === existingFeature)
    );
    if (existingPackSet) {
      config.set(existingPackSet.code, existingFeature);
    }
  });

  const packSetsToTry: IConfigurationOptionGroup[] = packSets.filter(packSet => !existingFeaturePackSets.find(p => p.code === packSet.code));
  let packSet = packSetsToTry.shift();
  let retryCount = 0;

  while (packSet && retryCount < packSets.length * 3) {
    // first try default value matching suggested features
    const suggestedValue = getSuggestedConfiguration(packSet, suggestedFeatures);
    if (suggestedValue && isNewOptionValid(Array.from(config.values()), config.size, suggestedValue, rules)) {
      config.set(packSet.code, suggestedValue);
    } else {
      let index = 0;
      let found = false;
      do {
        const defaultValue = getDefaultConfiguration(packSet, index);
        if (defaultValue !== suggestedValue && isNewOptionValid(Array.from(config.values()), config.size, defaultValue, rules)) {
          config.set(packSet.code, defaultValue);
          found = true;
        } else {
          index++;
        }
      } while (index < packSet.packs.length && !found);

      if (!found) {
        packSetsToTry.push(packSet);
        retryCount++;
      }
    }

    packSet = packSetsToTry.shift();
  }

  // Need to check that there is no packSet in packSetsToTry as well as packSet is empty
  if (packSetsToTry.length > 0 || packSet) {
    throw new Error('unable to determine default values');
  }

  return packSets.map(packSet => config.get(packSet.code) || '');
}
